import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { IdCard, RotateCw, Bell, Link2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Field, lbSelect } from "./LogbookTaskCard";
import HrTabs from "./HrTabs";
import { MyTasks, Given, Team, Assign } from "./LogbookBoards";
import LogbookAdmin from "./LogbookAdmin";
import { orgRoleLabel, fmtDateTime } from "./logbookUtils";

// HR → Logbook: Logbook's screens rebuilt in the ERP, on Logbook's live data
// (backend/hr_logbook.py relays to Logbook's own API — nothing is copied).
// Everything Logbook does except Time Tracking (clock in/out, attendance,
// live location), which stays out of the ERP. No separate Logbook sign-in:
// the ERP login is matched to the user's Logbook employee by the backend.

const REFRESH_MS = 30000;           // Logbook's own app refreshes every 30 s
const errText = (e) => formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong";

const TABS = ["mine", "given", "team", "assign", "admin"];

// No Logbook employee matches this ERP user. An ERP admin picks theirs once
// (no PIN — admins can set anyone's under Users & Roles anyway); everyone
// else is told who can set it.
function NotMatched({ link, onLinked }) {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (link.can_choose) api.get("/hr/logbook/accounts").then(({ data }) => setAccounts(data)).catch((e) => toast.error(errText(e)));
  }, [link.can_choose]);
  if (!link.can_choose) {
    return (
      <div className="max-w-xl rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-3" data-testid="lb-not-matched">
        No Logbook employee matches your ERP name ({user?.name}). Ask an admin to choose your Logbook employee under Users &amp; Roles.
      </div>
    );
  }
  const save = async () => {
    if (!pick) return toast.error("Choose your Logbook employee");
    setBusy(true);
    try { await api.put(`/hr/logbook/links/${user.id}`, { logbook_user_id: pick }); onLinked(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-md rounded-lg border border-slate-200 bg-white p-5 space-y-4" data-testid="lb-choose">
      <div>
        <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Link2 className="w-4 h-4 text-blue-600" />Which Logbook employee are you?</h2>
        <p className="text-sm text-slate-600 mt-1">No Logbook employee is named like your ERP account ({user?.name}). Choose yours once — no PIN needed.</p>
      </div>
      <Field label="Logbook employee">
        <select className={lbSelect} value={pick} onChange={(e) => setPick(e.target.value)} disabled={!accounts} data-testid="lb-choose-select">
          <option value="">{accounts ? "— Choose —" : "Loading…"}</option>
          {(accounts || []).map((a) => (
            <option key={a.id} value={a.id} disabled={!!a.linked_to && a.linked_to !== user?.email}>
              {a.name}{a.linked_to && a.linked_to !== user?.email ? ` (used by ${a.linked_to})` : ""}
            </option>
          ))}
        </select>
      </Field>
      <Button onClick={save} disabled={busy || !pick} data-testid="lb-choose-save">Continue</Button>
    </div>
  );
}

function Notifications() {
  const [list, setList] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/hr/logbook/notifications"); setList(data.notifications || []); } catch { /* non-fatal */ }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);
  const unread = list.filter((n) => !n.isRead).length;
  const post = async (action, body) => {
    try { await api.post(`/hr/logbook/action/${action}`, body); load(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="relative" aria-label="Logbook notifications" data-testid="lb-notifications">
          <Bell className="w-3.5 h-3.5" />
          {unread > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-red-600 text-white text-[10px] px-1 flex items-center justify-center">{unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <span className="text-sm font-semibold">Notifications</span>
          {unread > 0 && <button type="button" className="text-xs text-blue-600 hover:underline" onClick={() => post("mark_all_notifications_read", {})}>Mark all read</button>}
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
          {list.length === 0 ? <div className="px-3 py-6 text-center text-sm text-slate-500">No notifications</div> : list.map((n) => (
            <button key={n.id} type="button" onClick={() => !n.isRead && post("mark_notification_read", { notificationId: n.id })}
              className={`w-full text-left px-3 py-2 text-sm ${n.isRead ? "text-slate-500" : "text-slate-900 bg-blue-50/40"}`}>
              <div>{n.message}</div>
              <div className="text-[11px] font-mono text-slate-400">{fmtDateTime(n.createdAt)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Logbook() {
  const { tab = "mine" } = useParams();
  const location = useLocation();
  const [link, setLink] = useState(null);      // /hr/logbook/me
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const loadingRef = useRef(false);

  const loadMe = useCallback(async () => {
    try { const { data: d } = await api.get("/hr/logbook/me"); setLink(d); setError(""); } catch (e) { setError(errText(e)); }
  }, []);
  const reload = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try { const { data: d } = await api.get("/hr/logbook/data"); setData(d); setError(""); }
    catch (e) {
      if (e?.response?.status === 409) { setData(null); loadMe(); } else setError(errText(e));
    } finally { loadingRef.current = false; }
  }, [loadMe]);

  useEffect(() => { loadMe(); }, [loadMe]);
  useEffect(() => {
    if (!link?.linked) return undefined;
    reload();
    const id = setInterval(() => { if (!document.hidden) reload(); }, REFRESH_MS);
    return () => clearInterval(id);
  }, [link?.linked, reload]);

  const ctx = useMemo(() => {
    if (!data) return null;
    const names = Object.fromEntries(data.users.map((u) => [u.id, u.name]));
    return {
      me: data.me, data, reload,
      userName: (id) => names[id] || "Unassigned",
      toastOk: (m) => toast.success(m),
      toastError: (m) => { toast.error(m); return null; },
      errText,
      // One Logbook action; reloads the data afterwards unless opts.noReload/quiet.
      act: async (action, body, okMsg, opts = {}) => {
        try {
          const { data: res } = await api.post(`/hr/logbook/action/${action}`, body);
          if (okMsg) toast.success(okMsg);
          if (!opts.noReload && !opts.quiet) reload();
          return res || true;
        } catch (e) {
          toast.error(errText(e));
          return null;
        }
      },
    };
  }, [data, reload]);

  const me = data?.me;
  if (!TABS.includes(tab)) return <Navigate to="/hr/logbook/mine" replace />;

  return (
    <div className="space-y-4" data-testid="hr-logbook">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <IdCard className="w-5 h-5 text-blue-600" /> HR
          </h1>
          {me && <p className="text-xs text-slate-500 mt-0.5">Logbook employee: <b className="text-slate-700">{me.name}</b> · {orgRoleLabel(me.orgRole)}{me.isAdmin ? " · Admin" : ""}</p>}
        </div>
        {link?.linked && (
          <div className="flex items-center gap-2">
            <Notifications />
            <Button size="sm" variant="outline" onClick={reload} data-testid="lb-refresh"><RotateCw className="w-3.5 h-3.5 mr-1.5" />Refresh</Button>
          </div>
        )}
      </div>

      <HrTabs me={me} />
      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
      {link && !link.linked && <NotMatched link={link} onLinked={loadMe} />}

      {link?.linked && (
        <>
          {!ctx ? <div className="text-sm text-slate-500">Loading Logbook…</div> : (
            <>
              {tab === "mine" && <MyTasks ctx={ctx} />}
              {tab === "given" && <Given ctx={ctx} />}
              {tab === "team" && <Team ctx={ctx} />}
              {tab === "assign" && <Assign key={location.key} ctx={ctx} preselect={location.state?.assignee} />}
              {tab === "admin" && <LogbookAdmin ctx={ctx} />}
            </>
          )}
        </>
      )}
    </div>
  );
}
